import { createSlice, createAsyncThunk, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { LOADING_STATES, NOTIFICATION_SEVERITY } from '../../constants';
import { setStatusBar } from '../modals/modalSlice';
import { sharedThesaurusService, setAuthToken } from './sharedThesaurusAPI';
import { VOCABULAIRE_LABELS } from '../../components/thesaurus/vocabulaireLabels';
import {
    ThesaurusShare,
    EntreeShare,
    EntreeShareHistory,
    CreateThesaurusShareRequest,
    UpdateThesaurusShareRequest,
    CreateEntreeShareRequest,
    UpdateEntreeShareRequest,
    ThesaurusSharePermission,
    PaginatedHistoryResponse
} from '../../types/sharedThesaurus';

// État initial
export interface SharedThesaurusState {
    // Liste des thésaurus partagés
    ownedThesaurus: ThesaurusShare[];
    sharedWithMeThesaurus: ThesaurusShare[];
    
    // Thésaurus actif et ses entrées
    activeThesaurus: ThesaurusShare | null;
    activeThesaurusEntrees: EntreeShare[];

    /**
     * Toutes les entrées partagées accessibles, tous thésaurus confondus.
     *
     * Distinct de `activeThesaurusEntrees`, qui n'est peuplé que lorsqu'un
     * thésaurus partagé est ouvert : le menu `@` du chat doit pouvoir les
     * proposer sans qu'aucun thésaurus ne soit actif.
     */
    mentionableEntreeShares: EntreeShare[];
    mentionableEntreeSharesStatus: string;

    // Entrée active
    activeEntreeShare: EntreeShare | null;
    
    // Historique
    history: EntreeShareHistory[];
    historyPagination: {
        page: number;
        pages: number;
        total: number;
        hasNext: boolean;
        hasPrev: boolean;
    };
    
    // États de chargement
    status: string;
    entreeStatus: string;
    historyStatus: string;
    
    // Erreurs
    error: string | null;
    
    // Filtres et tri
    searchQuery: string;
    sortBy: 'nom' | 'dateModification' | 'dateCreation';
    sortOrder: 'asc' | 'desc';
}

const initialState: SharedThesaurusState = {
    ownedThesaurus: [],
    sharedWithMeThesaurus: [],
    activeThesaurus: null,
    activeThesaurusEntrees: [],
    mentionableEntreeShares: [],
    mentionableEntreeSharesStatus: LOADING_STATES.IDLE,
    activeEntreeShare: null,
    history: [],
    historyPagination: {
        page: 1,
        pages: 1,
        total: 0,
        hasNext: false,
        hasPrev: false
    },
    status: LOADING_STATES.IDLE,
    entreeStatus: LOADING_STATES.IDLE,
    historyStatus: LOADING_STATES.IDLE,
    error: null,
    searchQuery: '',
    sortBy: 'dateModification',
    sortOrder: 'desc'
};

// =====================
// Outils communs
// =====================

const T = VOCABULAIRE_LABELS.toasts;
/** Une phrase fixe par geste : jamais le message du serveur ni d'axios (détail en console). */
const E = VOCABULAIRE_LABELS.erreurs.partages;

/** Pose le jeton d'accès sur le service ; lève s'il manque. */
async function authentifier(instance: any): Promise<void> {
    const accessToken = await getTokenRedirect(tokenRequest, instance);
    if (!accessToken) throw new Error('Impossible d\'obtenir le token d\'accès');
    setAuthToken(accessToken);
}

/**
 * Phrase d'un refus pour un geste qui modifie : un refus de droit (403) le dit
 * (`refus`), plutôt que d'inviter à réessayer ; tout autre échec dit la phrase du geste.
 */
const phraseDeRefus = (error: unknown, phrase: string, refus: string = E.refusModification): string =>
    (error as { response?: { status?: number } } | null)?.response?.status === 403 ? refus : phrase;

type Dispatch = (action: ReturnType<typeof setStatusBar>) => unknown;

const signaler = (
    dispatch: Dispatch,
    message: string,
    severity: (typeof NOTIFICATION_SEVERITY)[keyof typeof NOTIFICATION_SEVERITY] = NOTIFICATION_SEVERITY.SUCCESS
) => {
    dispatch(setStatusBar({ open: true, message, severity }));
};

// =====================
// Async Thunks - Thésaurus
// =====================

export const fetchSharedThesaurus = createAsyncThunk(
    'sharedThesaurus/fetchAll',
    async (instance: any, { rejectWithValue }) => {
        try {
            await authentifier(instance);
            return await sharedThesaurusService.getAll();
        } catch (error) {
            console.error('❌ [fetchSharedThesaurus] Erreur:', error);
            return rejectWithValue(E.chargement);
        }
    }
);

/**
 * Charge toutes les entrées partagées accessibles (tous thésaurus confondus),
 * pour que le menu `@` du chat RAG puisse les proposer à la mention.
 *
 * Échec non bloquant : le chat reste utilisable avec les seules entrées
 * personnelles si l'appel échoue.
 */
export const fetchMentionableEntreeShares = createAsyncThunk(
    'sharedThesaurus/fetchMentionableEntreeShares',
    async (instance: any, { rejectWithValue }) => {
        try {
            await authentifier(instance);
            return await sharedThesaurusService.getAllEntreeShares();
        } catch (error) {
            console.error('❌ [fetchMentionableEntreeShares] Erreur:', error);
            return rejectWithValue(E.chargementMentions);
        }
    }
);

export const fetchSharedThesaurusById = createAsyncThunk(
    'sharedThesaurus/fetchById',
    async ({ id, instance }: { id: string; instance: any }, { rejectWithValue }) => {
        try {
            await authentifier(instance);
            return await sharedThesaurusService.getById(id);
        } catch (error) {
            console.error('❌ [fetchSharedThesaurusById] Erreur:', error);
            return rejectWithValue(E.chargementUn);
        }
    }
);

/** Crée un vocabulaire partagé, vide ou (avec `sourceThesaurusId`) à partir d'un vocabulaire personnel. */
export const createSharedThesaurus = createAsyncThunk(
    'sharedThesaurus/create',
    async ({ data, instance }: { data: CreateThesaurusShareRequest; instance: any }, { dispatch, rejectWithValue }) => {
        try {
            await authentifier(instance);
            const result = await sharedThesaurusService.create(data);
            signaler(dispatch, T.partageCree);
            return result;
        } catch (error) {
            console.error('❌ [createSharedThesaurus] Erreur:', error);
            return rejectWithValue(data.sourceThesaurusId ? E.partage : E.creation);
        }
    }
);

export const updateSharedThesaurus = createAsyncThunk(
    'sharedThesaurus/update',
    async ({ id, data, instance }: { id: string; data: UpdateThesaurusShareRequest; instance: any }, { dispatch, rejectWithValue }) => {
        try {
            await authentifier(instance);
            const result = await sharedThesaurusService.update(id, data);
            signaler(dispatch, T.partageModifie);
            return result;
        } catch (error) {
            console.error('❌ [updateSharedThesaurus] Erreur:', error);
            return rejectWithValue(phraseDeRefus(error, E.modification));
        }
    }
);

export const deleteSharedThesaurus = createAsyncThunk(
    'sharedThesaurus/delete',
    async ({ id, instance }: { id: string; instance: any }, { dispatch, rejectWithValue }) => {
        try {
            await authentifier(instance);
            await sharedThesaurusService.delete(id);
            signaler(dispatch, T.partageSupprime);
            return id;
        } catch (error) {
            console.error('❌ [deleteSharedThesaurus] Erreur:', error);
            return rejectWithValue(E.suppression);
        }
    }
);

// =====================
// Async Thunks - Participants
// =====================

export const addParticipant = createAsyncThunk(
    'sharedThesaurus/addParticipant',
    async ({ 
        thesaurusShareId, 
        participant, 
        instance 
    }: { 
        thesaurusShareId: string; 
        participant: {
            userId: string;
            displayName: string;
            email?: string;
            avatar?: string;
            permission?: ThesaurusSharePermission;
        };
        instance: any;
    }, { dispatch, rejectWithValue }) => {
        try {
            await authentifier(instance);
            const result = await sharedThesaurusService.addParticipant(thesaurusShareId, participant);
            signaler(dispatch, T.participantAjoute(participant.displayName));
            return result;
        } catch (error) {
            console.error('❌ [addParticipant] Erreur:', error);
            return rejectWithValue(phraseDeRefus(error, E.ajoutParticipant, E.refusParticipants));
        }
    }
);

export const removeParticipant = createAsyncThunk(
    'sharedThesaurus/removeParticipant',
    async ({ 
        thesaurusShareId, 
        participantUserId,
        participantName,
        instance 
    }: { 
        thesaurusShareId: string; 
        participantUserId: string;
        participantName: string;
        instance: any;
    }, { dispatch, rejectWithValue }) => {
        try {
            await authentifier(instance);
            const result = await sharedThesaurusService.removeParticipant(thesaurusShareId, participantUserId);
            signaler(dispatch, T.participantRetire(participantName));
            return result;
        } catch (error) {
            console.error('❌ [removeParticipant] Erreur:', error);
            return rejectWithValue(phraseDeRefus(error, E.retraitParticipant, E.refusParticipants));
        }
    }
);

export const updateParticipantPermission = createAsyncThunk(
    'sharedThesaurus/updateParticipantPermission',
    async ({ 
        thesaurusShareId, 
        participantUserId,
        permission,
        instance 
    }: { 
        thesaurusShareId: string; 
        participantUserId: string;
        permission: ThesaurusSharePermission;
        instance: any;
    }, { dispatch, rejectWithValue }) => {
        try {
            await authentifier(instance);
            const result = await sharedThesaurusService.updateParticipantPermission(
                thesaurusShareId, 
                participantUserId, 
                permission
            );
            signaler(dispatch, T.droitModifie);
            return result;
        } catch (error) {
            console.error('❌ [updateParticipantPermission] Erreur:', error);
            return rejectWithValue(phraseDeRefus(error, E.droitParticipant, E.refusParticipants));
        }
    }
);

// =====================
// Async Thunks - Entrées partagées
// =====================

export const fetchEntreeShares = createAsyncThunk(
    'sharedThesaurus/fetchEntreeShares',
    async ({ thesaurusShareId, search, instance }: { 
        thesaurusShareId: string; 
        search?: string;
        instance: any;
    }, { rejectWithValue }) => {
        try {
            await authentifier(instance);
            return await sharedThesaurusService.getEntreeShares(thesaurusShareId, search);
        } catch (error) {
            console.error('❌ [fetchEntreeShares] Erreur:', error);
            return rejectWithValue(E.chargementConcepts);
        }
    }
);

export const createEntreeShare = createAsyncThunk(
    'sharedThesaurus/createEntreeShare',
    async ({ data, instance }: { data: CreateEntreeShareRequest; instance: any }, { dispatch, rejectWithValue }) => {
        try {
            await authentifier(instance);
            const result = await sharedThesaurusService.createEntreeShare(data);
            signaler(dispatch, T.conceptCree);
            return result;
        } catch (error) {
            console.error('❌ [createEntreeShare] Erreur:', error);
            return rejectWithValue(phraseDeRefus(error, E.creationConcept));
        }
    }
);

export const updateEntreeShare = createAsyncThunk(
    'sharedThesaurus/updateEntreeShare',
    async ({ id, data, instance }: { id: string; data: UpdateEntreeShareRequest; instance: any }, { dispatch, rejectWithValue }) => {
        try {
            await authentifier(instance);
            const result = await sharedThesaurusService.updateEntreeShare(id, data);
            signaler(dispatch, T.conceptModifie);
            return result;
        } catch (error) {
            console.error('❌ [updateEntreeShare] Erreur:', error);
            return rejectWithValue(phraseDeRefus(error, E.modificationConcept));
        }
    }
);

export const deleteEntreeShare = createAsyncThunk(
    'sharedThesaurus/deleteEntreeShare',
    async ({ id, instance }: { id: string; instance: any }, { dispatch, rejectWithValue }) => {
        try {
            await authentifier(instance);
            await sharedThesaurusService.deleteEntreeShare(id);
            signaler(dispatch, T.conceptSupprime);
            return id;
        } catch (error) {
            console.error('❌ [deleteEntreeShare] Erreur:', error);
            return rejectWithValue(phraseDeRefus(error, E.suppressionConcept));
        }
    }
);

/**
 * Charge les entrées de TOUS les thésaurus partagés (owned + sharedWithMe)
 * Utilisé pour afficher les entrées partagées dans SecEntree
 */
export const fetchAllSharedEntrees = createAsyncThunk(
    'sharedThesaurus/fetchAllSharedEntrees',
    async ({ instance }: { instance: any }, { getState, rejectWithValue }) => {
        try {
            await authentifier(instance);

            const state = getState() as { sharedThesaurus: SharedThesaurusState };
            const { ownedThesaurus, sharedWithMeThesaurus } = state.sharedThesaurus;

            // Collecter tous les IDs de thésaurus partagés
            const allThesaurusIds = [
                ...ownedThesaurus.map(t => t._id),
                ...sharedWithMeThesaurus.map(t => t._id)
            ];

            if (allThesaurusIds.length === 0) {
                return [];
            }

            // Charger les entrées de tous les thésaurus en parallèle
            const results = await Promise.all(
                allThesaurusIds.map(id => 
                    sharedThesaurusService.getEntreeShares(id).catch(() => [])
                )
            );

            // Fusionner toutes les entrées
            return results.flat();
        } catch (error) {
            console.error('❌ [fetchAllSharedEntrees] Erreur:', error);
            return rejectWithValue(E.chargementMentions);
        }
    }
);

export const copyEntryToShared = createAsyncThunk(
    'sharedThesaurus/copyEntryToShared',
    async ({ thesaurusShareId, entreeId, instance }: { 
        thesaurusShareId: string; 
        entreeId: string;
        instance: any;
    }, { dispatch, rejectWithValue }) => {
        try {
            await authentifier(instance);
            const result = await sharedThesaurusService.copyEntryToShared(thesaurusShareId, entreeId);
            signaler(dispatch, T.conceptCopieVersPartage);
            return result;
        } catch (error) {
            console.error('❌ [copyEntryToShared] Erreur:', error);
            return rejectWithValue(phraseDeRefus(error, E.ajoutConcept));
        }
    }
);

/**
 * Copie un concept partagé parmi les concepts de l'utilisateur. `entreeShareId` est
 * l'identifiant du concept partagé (`POST /api/entree-shares/{id}`).
 *
 * Rejets : un conflit de nom (409) rejette par `{ conflict: true, existingEntryId }`
 * (contrat lu par la copie d'un vocabulaire entier) après un avertissement ; tout
 * autre échec, par la phrase du geste.
 */
export const copyToMyEntries = createAsyncThunk(
    'sharedThesaurus/copyToMyEntries',
    async ({ entreeShareId, instance }: { entreeShareId: string; instance: any }, { dispatch, rejectWithValue }) => {
        try {
            await authentifier(instance);
            const result = await sharedThesaurusService.copyToMyEntries(entreeShareId);
            signaler(dispatch, T.conceptCopieVersMoi);
            return result;
        } catch (error: any) {
            // Conflit de nom (409) : un concept du même nom existe déjà chez l'utilisateur.
            if (error?.response?.status === 409) {
                // Le nom du concept, entre guillemets dans la réponse ; jamais la phrase du serveur.
                const nom = String(error.response.data?.message ?? '').match(/"([^"]+)"/)?.[1];
                signaler(
                    dispatch,
                    nom ? T.conceptExisteDeja(nom) : T.conceptExisteDejaSansNom,
                    NOTIFICATION_SEVERITY.WARNING
                );
                return rejectWithValue({ conflict: true, existingEntryId: error.response.data?.existingEntreeId });
            }
            console.error('❌ [copyToMyEntries] Erreur:', error);
            return rejectWithValue(E.copieVersMoi);
        }
    }
);

// =====================
// Async Thunks - Historique
// =====================

export const fetchHistory = createAsyncThunk(
    'sharedThesaurus/fetchHistory',
    async ({ thesaurusShareId, options, instance }: { 
        thesaurusShareId: string;
        options?: {
            page?: number;
            limit?: number;
            action?: string;
            startDate?: string;
            endDate?: string;
        };
        instance: any;
    }, { rejectWithValue }) => {
        try {
            await authentifier(instance);
            return await sharedThesaurusService.getHistoryByThesaurus(thesaurusShareId, options);
        } catch (error) {
            console.error('❌ [fetchHistory] Erreur:', error);
            return rejectWithValue(E.historique);
        }
    }
);

export const fetchEntreeHistory = createAsyncThunk(
    'sharedThesaurus/fetchEntreeHistory',
    async ({ entreeShareId, options, instance }: { 
        entreeShareId: string;
        options?: {
            page?: number;
            limit?: number;
        };
        instance: any;
    }, { rejectWithValue }) => {
        try {
            await authentifier(instance);
            return await sharedThesaurusService.getHistoryByEntree(entreeShareId, options);
        } catch (error) {
            console.error('❌ [fetchEntreeHistory] Erreur:', error);
            return rejectWithValue(E.historique);
        }
    }
);

export const updateThesaurusSettings = createAsyncThunk(
    'sharedThesaurus/updateSettings',
    async ({ thesaurusShareId, settings, instance }: { 
        thesaurusShareId: string;
        settings: { historyRetentionMonths?: number };
        instance: any;
    }, { dispatch, rejectWithValue }) => {
        try {
            await authentifier(instance);
            const result = await sharedThesaurusService.updateSettings(thesaurusShareId, settings);
            signaler(dispatch, T.reglagesModifies);
            return result;
        } catch (error) {
            console.error('❌ [updateThesaurusSettings] Erreur:', error);
            return rejectWithValue(E.reglages);
        }
    }
);

// =====================
// Slice
// =====================

/** Gestes dont le refus s'affiche dans l'alerte de la liste (`state.error`). */
const GESTES_AFFICHES = [
    fetchSharedThesaurus,
    fetchSharedThesaurusById,
    createSharedThesaurus,
    deleteSharedThesaurus,
    fetchEntreeShares,
    fetchAllSharedEntrees,
] as const;

/**
 * Gestes dont le refus s'affiche dans leur fenêtre (toast, ou l'alerte propre de la fenêtre
 * de l'historique) ; jamais dans l'alerte de la liste. Leur départ efface celle-ci.
 */
const GESTES_EN_TOAST = [
    fetchHistory,
    updateSharedThesaurus,
    addParticipant,
    removeParticipant,
    updateParticipantPermission,
    createEntreeShare,
    updateEntreeShare,
    deleteEntreeShare,
    copyEntryToShared,
    copyToMyEntries,
    fetchEntreeHistory,
    updateThesaurusSettings,
] as const;

const sharedThesaurusSlice = createSlice({
    name: 'sharedThesaurus',
    initialState,
    reducers: {
        setActiveThesaurus(state, action: PayloadAction<ThesaurusShare | null>) {
            state.activeThesaurus = action.payload;
        },
        clearActiveThesaurus(state) {
            state.activeThesaurus = null;
            state.activeThesaurusEntrees = [];
        },
        setActiveEntreeShare(state, action: PayloadAction<EntreeShare | null>) {
            state.activeEntreeShare = action.payload;
        },
        setSearchQuery(state, action: PayloadAction<string>) {
            state.searchQuery = action.payload;
        },
        setSortConfig(state, action: PayloadAction<{ sortBy: 'nom' | 'dateModification' | 'dateCreation'; sortOrder: 'asc' | 'desc' }>) {
            state.sortBy = action.payload.sortBy;
            state.sortOrder = action.payload.sortOrder;
        },
        clearError(state) {
            state.error = null;
        },
        resetSharedThesaurusState(state) {
            return initialState;
        }
    },
    extraReducers: (builder) => {
        // Fetch all thesaurus
        builder
            .addCase(fetchSharedThesaurus.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
                state.error = null;
            })
            .addCase(fetchSharedThesaurus.fulfilled, (state, action) => {
                state.status = LOADING_STATES.SUCCEEDED;
                state.ownedThesaurus = action.payload.owned || [];
                state.sharedWithMeThesaurus = action.payload.sharedWithMe || [];
            })
            .addCase(fetchSharedThesaurus.rejected, (state) => {
                state.status = LOADING_STATES.FAILED;
            });

        // Entrées partagées mentionnables (menu @ du chat)
        builder
            .addCase(fetchMentionableEntreeShares.pending, (state) => {
                state.mentionableEntreeSharesStatus = LOADING_STATES.LOADING;
            })
            .addCase(fetchMentionableEntreeShares.fulfilled, (state, action) => {
                state.mentionableEntreeSharesStatus = LOADING_STATES.SUCCEEDED;
                state.mentionableEntreeShares = action.payload || [];
            })
            .addCase(fetchMentionableEntreeShares.rejected, (state) => {
                // Échec non bloquant : ne pas polluer `error`, qui pilote les
                // messages du module Thésaurus partagé. Le chat reste utilisable
                // avec les seules entrées personnelles.
                state.mentionableEntreeSharesStatus = LOADING_STATES.FAILED;
            });

        // Fetch by ID
        builder
            .addCase(fetchSharedThesaurusById.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
            })
            .addCase(fetchSharedThesaurusById.fulfilled, (state, action) => {
                state.status = LOADING_STATES.SUCCEEDED;
                state.activeThesaurus = action.payload;
                state.activeThesaurusEntrees = action.payload.entreeShares || [];
            })
            .addCase(fetchSharedThesaurusById.rejected, (state) => {
                state.status = LOADING_STATES.FAILED;
            });

        // Create thesaurus
        builder
            .addCase(createSharedThesaurus.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
            })
            .addCase(createSharedThesaurus.fulfilled, (state, action) => {
                state.status = LOADING_STATES.SUCCEEDED;
                state.ownedThesaurus.unshift(action.payload);
            })
            .addCase(createSharedThesaurus.rejected, (state) => {
                state.status = LOADING_STATES.FAILED;
            });

        // Update thesaurus
        builder
            .addCase(updateSharedThesaurus.fulfilled, (state, action) => {
                const index = state.ownedThesaurus.findIndex(t => t._id === action.payload._id);
                if (index !== -1) {
                    state.ownedThesaurus[index] = action.payload;
                }
                if (state.activeThesaurus?._id === action.payload._id) {
                    state.activeThesaurus = action.payload;
                }
            });

        // Delete thesaurus
        builder
            .addCase(deleteSharedThesaurus.fulfilled, (state, action) => {
                state.ownedThesaurus = state.ownedThesaurus.filter(t => t._id !== action.payload);
                if (state.activeThesaurus?._id === action.payload) {
                    state.activeThesaurus = null;
                    state.activeThesaurusEntrees = [];
                }
            });

        // Add/Remove/Update participant
        builder
            .addCase(addParticipant.fulfilled, (state, action) => {
                const index = state.ownedThesaurus.findIndex(t => t._id === action.payload._id);
                if (index !== -1) {
                    state.ownedThesaurus[index] = action.payload;
                }
                if (state.activeThesaurus?._id === action.payload._id) {
                    state.activeThesaurus = action.payload;
                }
            })
            .addCase(removeParticipant.fulfilled, (state, action) => {
                const index = state.ownedThesaurus.findIndex(t => t._id === action.payload._id);
                if (index !== -1) {
                    state.ownedThesaurus[index] = action.payload;
                }
                if (state.activeThesaurus?._id === action.payload._id) {
                    state.activeThesaurus = action.payload;
                }
            })
            .addCase(updateParticipantPermission.fulfilled, (state, action) => {
                const index = state.ownedThesaurus.findIndex(t => t._id === action.payload._id);
                if (index !== -1) {
                    state.ownedThesaurus[index] = action.payload;
                }
                if (state.activeThesaurus?._id === action.payload._id) {
                    state.activeThesaurus = action.payload;
                }
            });

        // Fetch entree shares
        builder
            .addCase(fetchEntreeShares.pending, (state) => {
                state.entreeStatus = LOADING_STATES.LOADING;
            })
            .addCase(fetchEntreeShares.fulfilled, (state, action) => {
                state.entreeStatus = LOADING_STATES.SUCCEEDED;
                state.activeThesaurusEntrees = action.payload;
            })
            .addCase(fetchEntreeShares.rejected, (state) => {
                state.entreeStatus = LOADING_STATES.FAILED;
            });

        // Fetch ALL shared entrees (from all thesaurus)
        builder
            .addCase(fetchAllSharedEntrees.pending, (state) => {
                state.entreeStatus = LOADING_STATES.LOADING;
            })
            .addCase(fetchAllSharedEntrees.fulfilled, (state, action) => {
                state.entreeStatus = LOADING_STATES.SUCCEEDED;
                state.activeThesaurusEntrees = action.payload;
            })
            .addCase(fetchAllSharedEntrees.rejected, (state) => {
                state.entreeStatus = LOADING_STATES.FAILED;
            });

        // Create entree share
        builder
            .addCase(createEntreeShare.fulfilled, (state, action) => {
                state.activeThesaurusEntrees.unshift(action.payload);
            });

        // Update entree share
        builder
            .addCase(updateEntreeShare.fulfilled, (state, action) => {
                const index = state.activeThesaurusEntrees.findIndex(e => e._id === action.payload._id);
                if (index !== -1) {
                    state.activeThesaurusEntrees[index] = action.payload;
                }
                if (state.activeEntreeShare?._id === action.payload._id) {
                    state.activeEntreeShare = action.payload;
                }
            });

        // Delete entree share
        builder
            .addCase(deleteEntreeShare.fulfilled, (state, action) => {
                state.activeThesaurusEntrees = state.activeThesaurusEntrees.filter(e => e._id !== action.payload);
                if (state.activeEntreeShare?._id === action.payload) {
                    state.activeEntreeShare = null;
                }
            });

        // Copy entry to shared
        builder
            .addCase(copyEntryToShared.fulfilled, (state, action) => {
                state.activeThesaurusEntrees.unshift(action.payload);
            });

        // Fetch history
        builder
            .addCase(fetchHistory.pending, (state) => {
                state.historyStatus = LOADING_STATES.LOADING;
            })
            .addCase(fetchHistory.fulfilled, (state, action) => {
                state.historyStatus = LOADING_STATES.SUCCEEDED;
                state.history = action.payload.data;
                state.historyPagination = action.payload.pagination;
            })
            .addCase(fetchHistory.rejected, (state) => {
                state.historyStatus = LOADING_STATES.FAILED;
            });

        // Fetch entree history
        builder
            .addCase(fetchEntreeHistory.fulfilled, (state, action) => {
                state.history = action.payload.data;
                state.historyPagination = action.payload.pagination;
            });

        // Update settings
        builder
            .addCase(updateThesaurusSettings.fulfilled, (state, action) => {
                const index = state.ownedThesaurus.findIndex(t => t._id === action.payload._id);
                if (index !== -1) {
                    state.ownedThesaurus[index] = action.payload;
                }
                if (state.activeThesaurus?._id === action.payload._id) {
                    state.activeThesaurus = action.payload;
                }
            });

        // Erreur affichée (alerte de la liste des partagés) : la phrase du geste
        // (`rejectWithValue`), effacée au geste suivant (`pending`) ou par `clearError`
        // à la fermeture d'une fenêtre. Les autres gestes (participants, concepts,
        // enregistrement, historique d'un concept, réglages) s'affichent en toast dans
        // leur fenêtre ; les mentions du chat échouent sans bruit.
        builder
            .addMatcher(isPending(...GESTES_AFFICHES, ...GESTES_EN_TOAST), (state) => {
                state.error = null;
            })
            .addMatcher(isRejected(...GESTES_AFFICHES), (state, action) => {
                state.error = typeof action.payload === 'string' ? action.payload : E.chargement;
            });
    }
});

export const {
    setActiveThesaurus,
    clearActiveThesaurus,
    setActiveEntreeShare,
    setSearchQuery,
    setSortConfig,
    clearError,
    resetSharedThesaurusState
} = sharedThesaurusSlice.actions;

export default sharedThesaurusSlice.reducer;
